import React, { useContext, useEffect, useState } from 'react';
import { Box, Flex, Spinner, Divider } from '@chakra-ui/react';

import { fetchActiveTransfers } from '../api/accounts';
import { getChainDetails } from '../api/chains';
import { convertTokenValue, notNull } from '../utils/core';
import { AppContext } from '../AppContext';

import { ActiveTransfer, GasPriceAlert, Text } from './index';

import Faqs from './Faqs';

export const reloadSlow = process.env.REACT_APP_RELOAD_SLOW_MS || '90000';
export const reloadFast = process.env.REACT_APP_RELOAD_FAST_MS || '15000';

const slow = parseInt(reloadSlow, 10);
const fast = parseInt(reloadFast, 10);
let pollers = [];

const GasPriceAlerts = ({ chains = {}, loading = false }) => {
  const alerts = Object.entries(chains)
    .filter(([, { feeData: { safeGasPrice, gasPriceLevel } = {} }]) =>
      safeGasPrice && gasPriceLevel !== 'low')
    .map(([id, { label, feeData: { safeGasPrice, gasPriceLevel } = {} }]) => ({
      id,
      label,
      gwei: convertTokenValue(safeGasPrice, 9),
      level: gasPriceLevel,
    }));
  if (!alerts.length) {
    return (<></>);
  }
  return (
    <Box>
      {alerts.map(({ id, label, gwei, level }) => (
        <GasPriceAlert key={id} chain={label} price={gwei} level={level} loading={loading} />
      ))}
    </Box>
  );
};

const ActiveTransfers = ({ address }) => {
  const { actions: { setActiveTransfers } } = useContext(AppContext);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingChains, setLoadingChains] = useState(true);
  const [transfers, setTransfers] = useState([]);
  const [chainDetails, setChainDetails] = useState({});
  const [reloadRate, setReloadRate] = useState(slow + 500);

  const loadChainInfo = async () => {
    try {
      setLoadingChains(true);
      const ids = new Set(transfers
        .map(({ origin: { name: origin } = {}, destination: { name = origin } = {} }) => name)
        .filter(notNull));
      const chains = await Promise.all([...ids].map(chainId => getChainDetails({ chainId })));
      setChainDetails(Object.fromEntries(chains.map(({ chain, ...rest }) => [chain, rest])));
      // @TODO - gas price warnings?
      setError(undefined);
      return chainDetails;
    } catch (e) {
      console.error('Failed to load chain info', e);
      setError(true);
      return [];
    } finally {
      setLoadingChains(false);
    }
  };

  const loadActiveTransfers = async () => {
    if (!address) {
      setTransfers([]);
      setActiveTransfers([]);
      return [];
    }
    try {
      let activeTransfers = await fetchActiveTransfers({ address });
      activeTransfers = activeTransfers || [];
      setTransfers(activeTransfers);
      // set in context to update dependent components
      setActiveTransfers(activeTransfers);
      const rate = activeTransfers.length ? fast : slow;
      if (rate !== reloadRate) {
        console.log(`Setting reload rate to: ${rate}`);
        setReloadRate(rate);
      }
      // don't await
      loadChainInfo();
      setError(undefined);
      return activeTransfers;
    } catch (e) {
      console.error('Failed to load active transfers', e);
      setError(true);
      return [];
    }
  };

  useEffect(async () => {
    if (!address) { return; }
    try {
      setChainDetails({});
      await loadActiveTransfers();
    } finally {
      setLoading(false);
    }
  }, [address, error]);

  // reload chain info when transfers change
  useEffect(() => { loadChainInfo(); }, [JSON.stringify(transfers)]);

  // enable polling
  useEffect(() => {
    const poller = setInterval(loadActiveTransfers, reloadRate);
    // flush any existing pollers before starting
    const active = pollers || [];
    pollers = [];
    active.forEach(p => clearInterval(p));
    pollers.push(poller);
    // don't leave poller running after dispose
    return () => clearInterval(poller);
  }, [address, reloadRate]);

  const TransferList = () => {
    if (loading) {
      return (
        <Flex justifyContent="center" alignItems="center">
          <Spinner />
          <Text textStyle="bodyGrey2" style={{ marginLeft: '1em' }}>loading...</Text>
        </Flex>
      );
    }
    return transfers.map(transfer => (
      <ActiveTransfer
        key={transfer.id || transfer.transferId}
        mb="40px"
        transfer={transfer}
        link={{ pathname: `/transfer/${transfer.id}`, state: JSON.stringify({ transfer }) }}
        transferOverviewStyle={{ flex: 1 }}
        eventLogsStyle={{ flex: 1 }}
        renderingMultiple
      />
    ));
  };
  return (
    <>
      {!transfers.length ? null : (
        <Box>
          <Text textStyle="h2" as="h2" mb="40px">Active Transfers</Text>
          <GasPriceAlerts chains={chainDetails} loading={loadingChains} />
          {error && (
            <Flex justifyContent="center" alignItems="center">
              <Text textStyle="errorMessage">Error: failed to load active transfers</Text>
            </Flex>
          )}
          <TransferList />
          <Divider borderColor="grey5" mb="40px" />
          <Faqs />
        </Box>
      )}
    </>
  );
};

export default ActiveTransfers;
